import React, { useState } from "react"
import { Col, Container, Row } from "react-bootstrap"
import PageWrapper from "@app/components/PageWrapper"
import Panel from "@app/components/Panel"
import { useFetcher, useNavigate, useParams, useRouteLoaderData } from "react-router-dom"
import { removeInspection, syncInspection } from "@app/models/inspection"
import { hasCompletedFollowUp, inspectionsByFilter, isCustomerInactive, setUpcomingCategory } from "@app/utils/inspections"
import { formatDate, getDayDiff } from "@app/utils/helper"
import Config from "@app/constants/Config"
import useOnline from "@app/lib/hooks/useOnline"
import Modal from "@app/components/Modal"
import Button from "@app/components/Button"
import FilterSearchSortHead from "@app/components/FilterSearchSortHead"
import useFilterSearch from "@app/components/FilterSearchSortHead/useFilterSearch"
import * as styles from "./inspections.module.scss"
import classNames from "classnames"

export async function loader () {
	return {}
}

export async function action ({ request }) {
	const formData = await request.formData()
	const uuid = await formData.get("uuid")
	const action = await formData.get("action")
	switch (action) {
		case "remove":
			return removeInspection(uuid)
		case "sync":
			return syncInspection(uuid)
	}
}

const useInspectionsByFilter = () => {
	const { inspections, office } = useRouteLoaderData("root")
	const { filter } = useParams()
	const res = inspectionsByFilter(inspections, filter, office)
	return res
}

function getLocation (data) {
	const {
		kundenundeinsatzort_einsatzort_adressetyp,
		kundenundeinsatzort_einsatzort_ort,
		kundenundeinsatzort_einsatzort_plz,
		kundenundeinsatzort_einsatzort_strasseundhausnummer,
		kundenundeinsatzort_kunde_ort,
		kundenundeinsatzort_kunde_plz,
		kundenundeinsatzort_kunde_strasseundhausnummer,
	} = data.values

	if (
		kundenundeinsatzort_einsatzort_adressetyp === "Einsatzwechseltätigkeit (Montage)"
		|| kundenundeinsatzort_einsatzort_adressetyp === "Abweichende Adresse"
	) {
		return `${kundenundeinsatzort_einsatzort_strasseundhausnummer}, ${kundenundeinsatzort_einsatzort_plz}, ${kundenundeinsatzort_einsatzort_ort}`
	} else if (kundenundeinsatzort_einsatzort_adressetyp === "Siehe Anschrift") {
		return `${kundenundeinsatzort_kunde_strasseundhausnummer}, ${kundenundeinsatzort_kunde_plz}, ${kundenundeinsatzort_kunde_ort}`
	} else if (typeof kundenundeinsatzort_einsatzort_adressetyp === "string" && kundenundeinsatzort_einsatzort_adressetyp.length > 0) {
		return `${kundenundeinsatzort_einsatzort_strasseundhausnummer}, ${kundenundeinsatzort_einsatzort_plz}, ${kundenundeinsatzort_einsatzort_ort}`
	} else {
		return "keine Adresse angegeben"
	}
}

function getJob (data) {
	const {
		taetigkeit_beschreibung_ueberlassungals,
	} = data.values
	return taetigkeit_beschreibung_ueberlassungals || "keine Tätigkeit angegeben"
}

function getUpcomingDueLabel (followupDate, upcomingCategory){
	if (upcomingCategory === "accident") {
		return "sofort"
	} else {
		const diff = getDayDiff(followupDate)
		if (diff < 0) {
			return "Überfällig"
		} else if (diff === 0) {
			return "Heute"
		} else if (diff === 1) {
			return "Morgen"
		} else if (diff >= 2) {
			return `Noch ${diff} Tage`
		}
	}
}

export default function Inspection () {
	const navigate = useNavigate()
	const fetcher = useFetcher()
	const { filter } = useParams()
	let inspectionsByCategory = useInspectionsByFilter()
	inspectionsByCategory = filter === "upcoming"
		? setUpcomingCategory(inspectionsByCategory)
		: inspectionsByCategory
	const defaultSort = filter === "upcoming"
		? { label: "Erstellungsdatum", value: "dueDate" }
		: { label: "Erstellungsdatum", value: "visitDate" }
	const { inspections, ...filterSearchProps } = useFilterSearch(inspectionsByCategory, defaultSort)
	const isOnline = useOnline()
	const [pendingDeleteId, setPendingDeleteId] = useState(null)

	const onSubmit = (action, uuid) => {
		fetcher.submit(
			{ action, uuid },
			{
				method: "post",
				encType: "application/x-www-form-urlencoded"
			}
		)
	}

	let headline, copy

	switch (filter) {
		case "initial-in-progress":
			headline = "Arbeitsplatzbesichtigungen in Bearbeitung"
			copy = "Beenden Sie bereits angefangene und gespeicherte Besichtigungen"
			break
		case "upcoming":
			headline = "Anstehende Folgebesichtigungen"
			copy = "Kommende Folgebesichtigungen"
			break
		case "follow-up-in-progress":
			headline = "Folgebesichtigungen in Bearbeitung"
			copy = "Beenden Sie bereits angefangene und gespeicherte Folgebesichtigungen"
			break
		case "unsynchronized":
			headline = "Ausstehende Zvoove-Übertragungen"
			copy = "Übertragen Sie bereits erfasste Besichtigungen"
			break
		case "archive":
			headline = "Abgeschlossene Arbeitsplatzbesichtigungen (Archiv)"
			copy = "Abgeschlossene Besichtigungen"
			break
	}

	return (<>
			<PageWrapper buttons={[
				{
					label: "Zurück zur Übersicht",
					onClick: () => {
						navigate(`${Config.BASE_PATH}/`)
					},
				},
			]}
						 vCenter={false}
						 valign={"center"}>
				<Container className={"mb-60"}>
					<Row>
						<Col className={"mb-36"}>
							<h1 className={"mb-0"}>{headline}</h1>
							<p className={"text-small text-dim"}>{copy}</p>
						</Col>
					</Row>
					<FilterSearchSortHead {...filterSearchProps} resultsCount={inspections.length} />
					<Row>
						{inspections.map((data) => {
							const {
								uuid,
								companyName,
								companyId,
								companyIdFormerly,
								customerState,
								visitDate,
								followupDate,
								followupInspection,
								upcomingCategory
							} = data
							let buttons = [
								{
									label: "Öffnen",
									type: "tertiary",
									onClick: () => {
										switch (filter) {
											case "upcoming":
												navigate(`${Config.BASE_PATH}/inspection/new-follow-up/${uuid}`)
												break
											default:
												navigate(`${Config.BASE_PATH}/inspection/${uuid}`)
										}
									}
								},
								{
									label: "Löschen",
									type: "quaternary",
									disabled: !isOnline,
									onClick: () => {
										setPendingDeleteId(uuid)
									}
								}
							]

							let cells = [
								visitDate !== undefined && {
									label: "Erstellungszeitpunkt",
									value: formatDate(visitDate),
								},
							]

							if (filter === "upcoming") {
								buttons = [
									{
										label: "Öffnen",
										type: "tertiary",
										onClick: () => {
											switch (filter) {
												case "upcoming":
													navigate(`${Config.BASE_PATH}/inspection/new-follow-up/${uuid}`)
													break
												default:
													navigate(`${Config.BASE_PATH}/inspection/${uuid}`)
											}
										}
									}
								]

								cells = [
									{
										label: getUpcomingDueLabel(followupDate, upcomingCategory),
										value: <span
											className={classNames(styles["highlight-date"], styles[`type-${upcomingCategory}`])}>{formatDate(followupDate)}</span>,
									},
								]
							}

							if (filter === "unsynchronized") {
								buttons = [
									{
										label: "Übertragen",
										type: "tertiary",
										disabled: !isOnline,
										onClick: () => {
											onSubmit("sync", uuid)
										}
									}
								]
							}

							if (filter === "archive") {
								buttons = [
									{
										label: "Öffnen",
										type: "tertiary",
										onClick: () => {
											navigate(`${Config.BASE_PATH}/inspection/${uuid}/archive`)
										}
									},
								]

								if (!followupInspection) {
									if (!isCustomerInactive(customerState)) {
										buttons.push({
											label: "Neue Folgebesichtigung",
											type: "quaternary",
											onClick: () => {
												navigate(`${Config.BASE_PATH}/inspection/new-follow-up/${uuid}`)
											}
										})
									}
								} else if (!hasCompletedFollowUp(inspectionsByCategory, followupInspection)) {
									buttons.push({
										label: "Folgebesichtigung öffnen",
										type: "quaternary",
										onClick: () => {
											navigate(`${Config.BASE_PATH}/inspection/${followupInspection}`)
										}
									})
								}
							}

							return (
								<Col key={uuid} xs={12}>
									<Panel
										hasBg={true}
										header={{
											label: (
												<>
													{`${companyName} (${companyId})`}
													{filter === "archive" && companyIdFormerly && (
														<span className={styles["former-company-id"]}>
															(ehem. Kd.-Nr. {companyIdFormerly})
														</span>
													)}
													{isCustomerInactive(customerState) && (
														<span className={styles["former-company-id"]}>Inaktiver Kunde</span>
													)}
												</>
											),
											cells,
										}}
										buttons={buttons}
									>
										{upcomingCategory && <div
											className={classNames(styles.highlight, styles[`type-${upcomingCategory}`])} />}
										{getJob(data)} - {getLocation(data)}
									</Panel>
								</Col>
							)
						})}
					</Row>
				</Container>
			</PageWrapper>
			<Modal
				show={!!pendingDeleteId}
				header={"Arbeitsplatzbegehung löschen"}
				buttons={<>
					<Button
						label={"Abbrechen"}
						type={"secondary"}
						callback={() => {
							setPendingDeleteId(null)
						}}
					/>
					<Button
						label={"endgültig löschen"}
						type={"warning"}
						callback={() => {
							onSubmit("remove", pendingDeleteId)
							setPendingDeleteId(null)
						}}
						isSubmit={true}
					/>
				</>}
			/>
		</>
	)
}
